import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsDownUp, ChevronsUpDown, Download, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { addDays, DAY_NAMES, formatDate, formatDays, formatMinutes, formatShortTime } from '../lib/format';
import { isPause, type AttendanceBreak, type DaySummary, type PresenceReason } from '../components/ShiftControl';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  Select,
  Table,
  Td,
  Th,
  TimeRange,
  Tr,
  statusTone,
  type BadgeTone,
} from '../components/ui';

/*
 * Attendance › Daily report (operator, 2026-10-27: "i need Attendance report daily for admin for
 * all team in deep details day by day filter by one day").
 *
 * One day, everyone in reach, in full: when each started and ended against the roster, every
 * break with its allowance and penalty, every stretch of Not working, worked time against the
 * Daily hours, and what the day came to. It reads the team board's own day
 * (`GET /attendance/board?details=true`), so the report and the board never disagree. A day at a
 * time — the day before, the day after — and a CSV of what is shown, for Excel.
 */

type DayStatus = 'WORKING' | 'ON_BREAK' | 'PAUSED' | 'ENDED' | 'ON_LEAVE' | 'OFF' | 'UPCOMING' | 'NOT_STARTED' | 'ABSENT' | 'NO_SCHEDULE';

interface PenaltyRef {
  id: string;
  days: string | number | null;
  status: string;
}

/** A stretch of Not working the Desktop Agent recorded on a shift, and the inactivity penalty it may have raised. */
interface Stretch {
  id: string;
  reason: PresenceReason;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  penaltyDueAt: string | null;
  penaltyOutcome: 'APPLIED' | 'CAP_REACHED' | 'NOT_REACHED' | 'DISABLED' | 'HOURS_MET' | null;
  penalty: PenaltyRef | null;
}

interface ReportShift {
  id: string;
  startedAt: string;
  endedAt: string | null;
  autoClosed: boolean;
  endUnconfirmed: boolean;
  closeNote: string | null;
  closedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  breaks: AttendanceBreak[];
  agentDevice?: { id: string; name: string } | null;
  idleSessions?: Stretch[];
}

interface ReportRow {
  user: { id: string; fullName: string; fullNameAr: string | null; employeeCode: string | null; jobTitle: string | null };
  department: { id: string; name: string; nameAr: string | null; code: string } | null;
  roster: { type: string; startAt: string | null; endAt: string | null; shiftName: string | null; shiftNameAr: string | null } | null;
  leave: { id: string; leaveType: { id: string; code: string; name: string; nameAr: string | null } } | null;
  status: DayStatus;
  /** The hours owed the day against Worked, or what it came to once judged — null when nothing is owed. */
  target: { requiredMinutes: number; state: 'MET' | 'IN_PROGRESS' | 'SHORT'; missingMinutes: number; extraMinutes: number; decided: boolean } | null;
  sessions: ReportShift[];
  summary: DaySummary;
  /** What the day came to once judged, the short-day penalty it charged, and how many penalties the hours dropped. */
  review?: {
    outcome: 'PENDING' | 'MET' | 'SHORT' | 'NOT_OWED';
    waivedCount: number;
    penalty: PenaltyRef | null;
    /** Its shift ended earlier than it allows (2026-11-03). Missing from an older API. */
    earlyEndPenalty?: PenaltyRef | null;
  } | null;
  /** The day's Free Shifts (CR-006): tracked, unpaid, never in `summary` or `target`. Missing from an older API. */
  free?: {
    count: number;
    workedMinutes: number;
    notWorkingMinutes: number;
    open: boolean;
    sessions: Array<{
      id: string;
      number: number;
      startedAt: string;
      endedAt: string | null;
      summary: { workedMinutes: number; notWorkingMinutes: number };
    }>;
  };
  /** Regular, Free and Total tracked worked minutes — the total is information only. */
  tracked?: { regularMinutes: number; freeMinutes: number; totalMinutes: number };
}

interface DayReport {
  date: string;
  today: string;
  now: string;
  holiday: { name: string; nameAr: string | null } | null;
  rows: ReportRow[];
}

/** The board's colours for the day's status, so a status reads the same on both. */
const STATUS_TONE: Record<DayStatus, BadgeTone> = {
  WORKING: 'success',
  ON_BREAK: 'warning',
  PAUSED: 'info',
  ENDED: 'neutral',
  ON_LEAVE: 'info',
  OFF: 'neutral',
  UPCOMING: 'neutral',
  NOT_STARTED: 'warning',
  ABSENT: 'error',
  NO_SCHEDULE: 'neutral',
};

/**
 * The employee column stays in view while a wide table scrolls sideways on a laptop
 * screen. A shadow, not a border, marks its edge: a collapsed table's borders stay behind.
 */
const PINNED = 'sticky start-0 z-[1] shadow-[inset_-1px_0_0_var(--line)] rtl:shadow-[inset_1px_0_0_var(--line)]';

/** Whom the table shows — the figures above it always count the whole day. */
const SHOWS = ['ALL', 'CAME', 'LATE', 'LEFT_EARLY', 'ABSENT', 'LEAVE', 'OFF', 'SHORT', 'NOT_WORKING', 'PENALTIES'] as const;
type Show = (typeof SHOWS)[number];

type PenaltyKind = 'BREAK' | 'IDLE' | 'SHORT_DAY' | 'EARLY_END';

/**
 * Every penalty the day raised — a break past its limit, Not working, a short day, a shift
 * ended earlier than it allows — with where it stands.
 */
function dayPenalties(row: ReportRow): Array<PenaltyRef & { kind: PenaltyKind }> {
  const list: Array<PenaltyRef & { kind: PenaltyKind }> = [];
  for (const shift of row.sessions) {
    for (const b of shift.breaks) if (b.penalty) list.push({ ...b.penalty, kind: 'BREAK' });
    for (const i of shift.idleSessions ?? []) if (i.penalty) list.push({ ...i.penalty, kind: 'IDLE' });
  }
  if (row.review?.penalty) list.push({ ...row.review.penalty, kind: 'SHORT_DAY' });
  if (row.review?.earlyEndPenalty) list.push({ ...row.review.earlyEndPenalty, kind: 'EARLY_END' });
  return list;
}

/** Came, and worked less than the Daily hours — an absence is counted as one, not as a short day. */
const cameShort = (row: ReportRow) => row.sessions.length > 0 && row.target?.state === 'SHORT';

/** The days of salary those penalties take — a cancelled one takes none. */
const chargedDays = (penalties: PenaltyRef[]) =>
  penalties.filter((p) => p.status !== 'CANCELLED').reduce((sum, p) => sum + Number(p.days ?? 0), 0);

function matches(row: ReportRow, show: Show): boolean {
  switch (show) {
    case 'CAME':
      return row.sessions.length > 0;
    case 'LATE':
      // Rostered, the shift begun, not started: late too, as the board calls it.
      return (row.summary.lateMinutes ?? 0) > 0 || row.status === 'NOT_STARTED';
    case 'LEFT_EARLY':
      return (row.summary.leftEarlyMinutes ?? 0) > 0;
    case 'ABSENT':
      return row.status === 'ABSENT';
    case 'LEAVE':
      return row.status === 'ON_LEAVE';
    case 'OFF':
      return row.status === 'OFF' || row.status === 'NO_SCHEDULE';
    case 'SHORT':
      return cameShort(row);
    case 'NOT_WORKING':
      return row.summary.notWorkingMinutes > 0;
    case 'PENALTIES':
      // Penalties that stand, as the column counts them: one dropped or cancelled takes nothing.
      return chargedDays(dayPenalties(row)) > 0;
    default:
      return true;
  }
}

const MINUTE = 60_000;
const ms = (value: string) => new Date(value).getTime();
const minutesBetween = (from: number, to: number) => Math.max(0, Math.floor((to - from) / MINUTE));

/**
 * A break's or a stretch's own span, cut to its shift, as worked time counts it: one still open
 * runs to the shift's end, or now.
 */
function spanOf(item: { startedAt: string; endedAt: string | null }, shift: ReportShift, now: number) {
  const shiftEnd = shift.endedAt ? ms(shift.endedAt) : now;
  const from = Math.max(ms(item.startedAt), ms(shift.startedAt));
  const to = Math.min(item.endedAt ? ms(item.endedAt) : shiftEnd, shiftEnd);
  return { from, to: Math.max(from, to), minutes: minutesBetween(from, to) };
}

/** Minutes a break ran past its type's allowance — 0 within it, or for a type with none. */
const overBy = (minutes: number, max: number | null) => (max == null ? 0 : Math.max(0, minutes - max));

const stretchesOf = (row: ReportRow) => row.sessions.reduce((n, s) => n + (s.idleSessions?.length ?? 0), 0);
const breaksOf = (row: ReportRow) => row.sessions.reduce((n, s) => n + s.breaks.filter((b) => !isPause(b)).length, 0);

/** "09:05" on a 24-hour clock, in the browser's time like every time on the page — for the CSV. */
const CLOCK = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const clock = (value: string | number | null | undefined) => (value == null ? '' : CLOCK.format(new Date(value)));

/** A cell as Excel reads it safely: quoted when it must be, and never taken for a formula. */
function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (typeof value === 'string' && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function saveCsv(name: string, rows: Array<Array<string | number | null | undefined>>) {
  // The byte-order mark is what makes Excel read the file as UTF-8 — Arabic names included.
  const body = `﻿${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** The hours the day's bars span: every roster and every shift, out to whole hours. */
function dayRange(rows: ReportRow[], now: number): { from: number; to: number } | null {
  let from = Infinity;
  let to = -Infinity;
  for (const row of rows) {
    if (row.roster?.startAt && row.roster.endAt) {
      from = Math.min(from, ms(row.roster.startAt));
      to = Math.max(to, ms(row.roster.endAt));
    }
    for (const shift of row.sessions) {
      from = Math.min(from, ms(shift.startedAt));
      to = Math.max(to, shift.endedAt ? ms(shift.endedAt) : now);
    }
  }
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const start = new Date(from);
  start.setMinutes(0, 0, 0);
  const end = new Date(to);
  if (end.getMinutes() || end.getSeconds() || end.getMilliseconds()) end.setHours(end.getHours() + 1, 0, 0, 0);
  return { from: start.getTime(), to: end.getTime() };
}

export function DailyReport() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? '';
  const departmentId = params.get('departmentId') ?? '';
  const requested = params.get('show') as Show | null;
  const show: Show = requested && SHOWS.includes(requested) ? requested : 'ALL';
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  // The width of the table in view: an opened day is pinned to it, so it reads
  // without scrolling sideways however wide the table runs on a narrow screen.
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  useEffect(() => {
    if (!frame) return;
    const observer = new ResizeObserver(([entry]) => setFrameWidth(entry.contentRect.width));
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frame]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const { data: departments } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => api.get<Paginated<{ id: string; name: string; nameAr: string | null }>>('/departments', { pageSize: 100 }),
  });

  const report = useQuery({
    queryKey: ['attendance', 'report', date, departmentId],
    queryFn: () =>
      api.get<DayReport>('/attendance/board', { date: date || undefined, departmentId: departmentId || undefined, details: true }),
    // Today's figures move while the day runs; a day gone by is settled.
    refetchInterval: (q) => (q.state.data && q.state.data.date !== q.state.data.today ? false : 60_000),
  });

  const data = report.data;
  const day = data?.date ?? date;
  const today = data?.today ?? null;
  const live = !!data && data.date === data.today;
  const now = data ? ms(data.now) : Date.now();
  // The day goes in the address only when it is not today, as on the board.
  const goTo = (value: string) => setFilter('date', value && value !== today ? value : '');

  // By department, then by name — a report reads down the teams.
  const rows = useMemo(() => {
    const collator = new Intl.Collator(locale);
    return [...(data?.rows ?? [])].sort((a, b) => {
      if (!a.department !== !b.department) return a.department ? -1 : 1;
      const byDepartment = a.department && b.department ? collator.compare(localized(a.department), localized(b.department)) : 0;
      return byDepartment || collator.compare(localized(a.user), localized(b.user));
    });
  }, [data, locale, localized]);

  const needle = search.trim().toLowerCase();
  const visible = rows.filter(
    (row) =>
      matches(row, show) &&
      (!needle ||
        [row.user.fullName, row.user.fullNameAr, row.user.employeeCode].some((v) => v?.toLowerCase().includes(needle))),
  );
  const range = useMemo(() => (data ? dayRange(data.rows, ms(data.now)) : null), [data]);

  const totals = useMemo(() => {
    const sum = {
      team: rows.length,
      rostered: 0,
      came: 0,
      late: 0,
      absent: 0,
      notStarted: 0,
      leave: 0,
      off: 0,
      worked: 0,
      breaks: 0,
      over: 0,
      notWorking: 0,
      stretches: 0,
      short: 0,
      penaltyDays: 0,
    };
    for (const row of rows) {
      const s = row.summary;
      if (row.roster?.startAt) sum.rostered += 1;
      if (row.sessions.length) sum.came += 1;
      if ((s.lateMinutes ?? 0) > 0) sum.late += 1;
      if (row.status === 'ABSENT') sum.absent += 1;
      if (row.status === 'NOT_STARTED') sum.notStarted += 1;
      if (row.status === 'ON_LEAVE') sum.leave += 1;
      if (row.status === 'OFF') sum.off += 1;
      if (cameShort(row)) sum.short += 1;
      sum.worked += s.workedMinutes;
      sum.breaks += s.breakMinutes;
      sum.over += s.overrunMinutes;
      sum.notWorking += s.notWorkingMinutes;
      sum.stretches += stretchesOf(row);
      sum.penaltyDays += chargedDays(dayPenalties(row));
    }
    return sum;
  }, [rows]);

  const expandable = visible.filter((row) => row.sessions.length > 0);
  const allOpen = expandable.length > 0 && expandable.every((row) => open.has(row.user.id));
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(expandable.map((row) => row.user.id)));

  const span = (minutes: number) => formatMinutes(minutes, locale);

  const download = () => {
    if (!data) return;
    const minutes = (label: string) => `${label} (${t('attendance.report.minutesUnit')})`;
    const header = [
      t('attendance.day'),
      t('common.employee'),
      t('employees.code'),
      t('common.department'),
      t('employees.jobTitle'),
      t('attendance.roster'),
      t('attendance.report.rosterStart'),
      t('attendance.report.rosterEnd'),
      t('common.status'),
      t('attendance.report.leave'),
      t('attendance.started'),
      minutes(t('attendance.late')),
      t('attendance.ended'),
      minutes(t('attendance.leftEarly')),
      t('attendance.report.shifts'),
      t('attendance.report.breakCount'),
      minutes(t('attendance.breaks')),
      minutes(t('attendance.overrun')),
      minutes(t('attendance.paused')),
      minutes(t('attendance.notWorking')),
      minutes(t('attendance.worked')),
      // Beside Worked, never in it (CR-006): the Free Shifts, and the two together.
      t('attendance.report.freeCount'),
      minutes(t('attendance.report.freeWorked')),
      minutes(t('attendance.trackedTotal')),
      minutes(t('attendance.dailyHours')),
      t('attendance.report.result'),
      t('attendance.report.penaltyDays'),
      t('attendance.report.breakDetail'),
      t('attendance.report.notWorkingDetail'),
      t('attendance.report.freeDetail'),
      t('attendance.report.notes'),
    ];
    const lines = visible.map((row) => {
      const s = row.summary;
      const breaks: string[] = [];
      const stretches: string[] = [];
      const notes: string[] = [];
      for (const shift of row.sessions) {
        for (const b of shift.breaks) {
          const { from, to, minutes: length } = spanOf(b, shift, now);
          const over = isPause(b) ? 0 : overBy(length, b.breakType.maxMinutes);
          breaks.push(
            `${isPause(b) ? t('attendance.paused') : localized(b.breakType)} ${clock(from)}–${b.endedAt || shift.endedAt ? clock(to) : '…'} (${span(length)}${over ? `, +${span(over)}` : ''})`,
          );
        }
        for (const i of shift.idleSessions ?? []) {
          const { from, to, minutes: length } = spanOf(i, shift, now);
          stretches.push(
            `${clock(from)}–${i.endedAt || shift.endedAt ? clock(to) : '…'} ${t(`presence.reason.${i.reason}` as TranslationKey)} (${span(length)})`,
          );
        }
        if (shift.endUnconfirmed) notes.push(t('attendance.endUnconfirmed'));
        else if (shift.autoClosed) notes.push(t('attendance.autoClosed'));
        else if (shift.closedBy) {
          notes.push(`${t('attendance.closedBy', { name: localized(shift.closedBy) })}${shift.closeNote ? `: ${shift.closeNote}` : ''}`);
        }
      }
      const target = row.target;
      return [
        data.date,
        localized(row.user),
        row.user.employeeCode,
        row.department ? localized(row.department) : '',
        row.user.jobTitle,
        row.roster?.type === 'OFF'
          ? t('attendance.status.OFF')
          : row.roster
            ? localized({ name: row.roster.shiftName ?? '', nameAr: row.roster.shiftNameAr })
            : '',
        clock(row.roster?.startAt),
        clock(row.roster?.endAt),
        t(`attendance.status.${row.status}` as TranslationKey),
        row.leave ? localized(row.leave.leaveType) : '',
        clock(s.firstStartAt),
        s.lateMinutes,
        clock(s.lastEndAt),
        s.leftEarlyMinutes,
        row.sessions.length,
        breaksOf(row),
        s.breakMinutes,
        s.overrunMinutes,
        s.pausedMinutes ?? 0,
        s.notWorkingMinutes,
        s.workedMinutes,
        row.free?.count ?? 0,
        row.free?.workedMinutes ?? 0,
        row.tracked?.totalMinutes ?? s.workedMinutes,
        target?.requiredMinutes ?? '',
        !target
          ? ''
          : target.state === 'MET'
            ? t('attendance.targetMet')
            : target.state === 'SHORT'
              ? t('attendance.targetShort', { minutes: span(target.missingMinutes) })
              : t('attendance.targetToGo', { minutes: span(target.missingMinutes) }),
        chargedDays(dayPenalties(row)) || '',
        breaks.join('; '),
        stretches.join('; '),
        (row.free?.sessions ?? [])
          .map((f) => `#${f.number} ${clock(f.startedAt)}–${f.endedAt ? clock(f.endedAt) : '…'} (${span(f.summary.workedMinutes)})`)
          .join('; '),
        notes.join('; '),
      ];
    });
    saveCsv(`attendance-${data.date}.csv`, [header, ...lines]);
  };

  return (
    <div className="space-y-4">
      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('attendance.report.previousDay')}
              title={t('attendance.report.previousDay')}
              disabled={!day}
              onClick={() => goTo(addDays(day, -1))}
            >
              <ChevronLeft className="h-4 w-4 flip-rtl" aria-hidden />
            </Button>
            <Input
              type="date"
              aria-label={t('attendance.day')}
              value={day}
              max={today ?? undefined}
              onChange={(e) => goTo(e.target.value)}
              className="!w-auto"
            />
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('attendance.report.nextDay')}
              title={t('attendance.report.nextDay')}
              disabled={!day || (today !== null && day >= today)}
              onClick={() => goTo(addDays(day, 1))}
            >
              <ChevronRight className="h-4 w-4 flip-rtl" aria-hidden />
            </Button>
            {data && !live && (
              <Button variant="secondary" size="sm" className="ms-1" onClick={() => goTo(data.today)}>
                {t('attendance.today')}
              </Button>
            )}
          </div>
          <Select
            value={departmentId}
            onChange={(e) => setFilter('departmentId', e.target.value)}
            aria-label={t('common.department')}
            className="sm:!w-auto sm:min-w-[12rem]"
          >
            <option value="">{t(can(P.USERS_VIEW_ALL) ? 'attendance.allDepartments' : 'reach.allMyDepartments')}</option>
            {departments?.items.map((d) => (
              <option key={d.id} value={d.id}>
                {localized(d)}
              </option>
            ))}
          </Select>
          <Select
            value={show}
            onChange={(e) => setFilter('show', e.target.value === 'ALL' ? '' : e.target.value)}
            aria-label={t('attendance.report.show')}
            className="sm:!w-auto sm:min-w-[12rem]"
          >
            {SHOWS.map((s) => (
              <option key={s} value={s}>
                {t(`attendance.report.show.${s}` as TranslationKey)}
              </option>
            ))}
          </Select>
          <div className="relative min-w-[12rem] grow sm:max-w-xs">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('attendance.report.search')}
              aria-label={t('attendance.report.search')}
              className="ps-9"
            />
          </div>
        </div>
        {data && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t hairline px-4 py-2.5">
            <span className="text-body-sm font-medium text-[var(--text-primary)]">
              {DAY_NAMES[locale][new Date(`${data.date}T00:00:00Z`).getUTCDay()]} · {formatDate(`${data.date}T00:00:00`, locale)}
            </span>
            {data.holiday && <Badge tone="plasma">{t('attendance.holiday', { name: localized(data.holiday) })}</Badge>}
            {live && <span className="text-caption text-muted">{t('attendance.report.asOf', { time: formatShortTime(data.now, locale) })}</span>}
            <span className="ms-auto inline-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="ghost"
                icon={allOpen ? <ChevronsDownUp className="h-4 w-4" aria-hidden /> : <ChevronsUpDown className="h-4 w-4" aria-hidden />}
                disabled={!expandable.length}
                onClick={toggleAll}
              >
                {allOpen ? t('attendance.report.collapseAll') : t('attendance.report.expandAll')}
              </Button>
              <Button size="sm" icon={<Download className="h-4 w-4" aria-hidden />} disabled={!visible.length} onClick={download}>
                {t('attendance.report.csv')}
              </Button>
            </span>
          </div>
        )}
      </Card>

      {data && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            label={t('attendance.report.team')}
            value={totals.team}
            hint={t('attendance.report.rostered', { count: totals.rostered })}
            onClick={() => setFilter('show', '')}
          />
          <Metric
            label={t('attendance.report.came')}
            value={totals.came}
            hint={t('attendance.report.lateCount', { count: totals.late })}
            tone={totals.came ? 'success' : 'neutral'}
            onClick={() => setFilter('show', 'CAME')}
          />
          <Metric
            label={t('attendance.report.absent')}
            value={totals.absent}
            hint={totals.notStarted ? t('attendance.report.notStartedCount', { count: totals.notStarted }) : undefined}
            tone={totals.absent ? 'error' : 'neutral'}
            onClick={() => setFilter('show', 'ABSENT')}
          />
          <Metric
            label={t('attendance.report.onLeave')}
            value={totals.leave}
            hint={t('attendance.report.dayOffCount', { count: totals.off })}
            onClick={() => setFilter('show', 'LEAVE')}
          />
          <Metric
            label={t('attendance.worked')}
            value={span(totals.worked)}
            hint={totals.came ? t('attendance.report.average', { minutes: span(Math.round(totals.worked / totals.came)) }) : undefined}
          />
          <Metric
            label={t('attendance.breaks')}
            value={span(totals.breaks)}
            hint={totals.over ? t('attendance.report.overAllowance', { minutes: span(totals.over) }) : undefined}
            tone={totals.over ? 'warning' : 'neutral'}
          />
          <Metric
            label={t('attendance.notWorking')}
            value={span(totals.notWorking)}
            hint={totals.stretches ? t('attendance.report.stretchCount', { count: totals.stretches }) : undefined}
            tone={totals.notWorking ? 'error' : 'neutral'}
            onClick={() => setFilter('show', 'NOT_WORKING')}
          />
          <Metric
            label={t('attendance.report.penalties')}
            value={totals.penaltyDays ? formatDays(totals.penaltyDays, locale) : '—'}
            hint={totals.short ? t('attendance.report.shortCount', { count: totals.short }) : undefined}
            tone={totals.penaltyDays ? 'error' : 'neutral'}
            onClick={() => setFilter('show', 'PENALTIES')}
          />
        </div>
      )}

      <Card padded={false}>
        {report.isLoading ? (
          <LoadingBlock />
        ) : report.error ? (
          <ErrorState error={report.error} onRetry={() => void report.refetch()} />
        ) : !visible.length ? (
          <EmptyState />
        ) : (
          <div ref={setFrame}>
            <Table>
              <thead>
                <tr>
                  <Th className={clsx(PINNED, 'bg-[var(--surface-card)]')}>{t('common.employee')}</Th>
                  <Th>{t('attendance.roster')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('attendance.started')}</Th>
                  <Th>{t('attendance.ended')}</Th>
                  <Th>{t('attendance.breaks')}</Th>
                  <Th align="end">{t('attendance.notWorking')}</Th>
                  <Th align="end">{t('attendance.worked')}</Th>
                  <Th align="end">{t('attendance.dailyHours')}</Th>
                  <Th align="end">{t('attendance.report.penalties')}</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <Fragment key={row.user.id}>
                    <ReportLine row={row} open={open.has(row.user.id)} onToggle={() => toggle(row.user.id)} />
                    {open.has(row.user.id) && (
                      <tr>
                        <td colSpan={10} className="border-b hairline bg-[var(--surface-sunken)] p-0">
                          <div className="sticky start-0 px-3 py-4 sm:px-5" style={frameWidth ? { width: frameWidth } : undefined}>
                            <DayDetail row={row} range={range} now={now} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** One person's day on one line: the roster, the times, the totals, what it came to. */
function ReportLine({ row, open, onToggle }: { row: ReportRow; open: boolean; onToggle: () => void }) {
  const { t, locale, localized } = useI18n();
  const s = row.summary;
  const span = (minutes: number) => formatMinutes(minutes, locale);
  const came = row.sessions.length > 0;
  const penalties = dayPenalties(row);
  const days = chargedDays(penalties);
  const breaks = breaksOf(row);
  const stretches = stretchesOf(row);
  const target = row.target;

  return (
    <Tr className={open ? 'bg-[var(--surface-sunken)]' : undefined}>
      <Td className={clsx(PINNED, 'min-w-[13rem]', open ? 'bg-[var(--surface-sunken)]' : 'bg-[var(--surface-card)]')}>
        <span className="flex items-start gap-2">
          {came ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              aria-label={t('attendance.report.toggle', { name: localized(row.user) })}
              className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-xs text-muted hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]"
            >
              <ChevronDown className={clsx('h-4 w-4 transition-transform duration-fast', !open && '-rotate-90 rtl:rotate-90')} aria-hidden />
            </button>
          ) : (
            <span className="h-5 w-5 shrink-0" aria-hidden />
          )}
          <span className="min-w-0">
            <span className="block font-medium">{localized(row.user)}</span>
            <span className="block text-caption text-faint">
              {[row.user.employeeCode, row.department ? localized(row.department) : null, row.user.jobTitle].filter(Boolean).join(' · ')}
            </span>
          </span>
        </span>
      </Td>
      <Td className="text-muted">
        {row.roster?.startAt ? (
          <>
            <span className="block">{localized({ name: row.roster.shiftName ?? '', nameAr: row.roster.shiftNameAr })}</span>
            <TimeRange from={row.roster.startAt} to={row.roster.endAt} className="tabular text-caption" />
          </>
        ) : row.roster?.type === 'OFF' ? (
          t('attendance.status.OFF')
        ) : (
          <span className="text-faint">—</span>
        )}
      </Td>
      <Td>
        <Badge tone={STATUS_TONE[row.status] ?? 'neutral'} dot={row.status === 'WORKING' || row.status === 'ON_BREAK' || row.status === 'PAUSED'}>
          {t(`attendance.status.${row.status}` as TranslationKey)}
        </Badge>
        {row.leave && <span className="mt-0.5 block text-caption text-muted">{localized(row.leave.leaveType)}</span>}
      </Td>
      <Td className="tabular text-muted">
        {s.firstStartAt ? formatShortTime(s.firstStartAt, locale) : '—'}
        {(s.lateMinutes ?? 0) > 0 && (
          <span className="block whitespace-nowrap text-caption text-warning-fg dark:text-warning-dark">
            {t('attendance.late')} {span(s.lateMinutes ?? 0)}
          </span>
        )}
      </Td>
      <Td className="tabular text-muted">
        {s.lastEndAt ? formatShortTime(s.lastEndAt, locale) : s.open ? <Badge tone="success" dot>{t('attendance.stillOpen')}</Badge> : '—'}
        {(s.leftEarlyMinutes ?? 0) > 0 && (
          <span className="block whitespace-nowrap text-caption text-warning-fg dark:text-warning-dark">
            {t('attendance.leftEarly')} {span(s.leftEarlyMinutes ?? 0)}
          </span>
        )}
        {row.sessions.some((x) => x.endUnconfirmed) ? (
          <span className="block text-caption text-warning-fg dark:text-warning-dark">{t('attendance.endUnconfirmed')}</span>
        ) : row.sessions.some((x) => x.autoClosed) ? (
          <span className="block text-caption text-warning-fg dark:text-warning-dark">{t('attendance.autoClosed')}</span>
        ) : null}
        {row.sessions.length > 1 && (
          <span className="block text-caption text-faint">{t('attendance.report.shiftCount', { count: row.sessions.length })}</span>
        )}
      </Td>
      <Td className="tabular text-muted">
        {s.breakMinutes > 0 ? (
          <span className="whitespace-nowrap">
            {span(s.breakMinutes)}
            <span className="text-caption text-faint"> · {breaks}</span>
          </span>
        ) : (
          '—'
        )}
        {s.overrunMinutes > 0 && (
          <span className="block whitespace-nowrap text-caption text-error-fg dark:text-error-dark">+{span(s.overrunMinutes)}</span>
        )}
        {/* A meeting's time stayed in Worked, as on the board. */}
        {(s.workBreakMinutes ?? 0) > 0 && (
          <span className="block whitespace-nowrap text-caption text-muted">
            {t('attendance.workBreaks', { minutes: span(s.workBreakMinutes ?? 0) })}
          </span>
        )}
        {(s.pausedMinutes ?? 0) > 0 && (
          <span className="block whitespace-nowrap text-caption text-muted">
            {t('attendance.pausedFor', { minutes: span(s.pausedMinutes ?? 0) })}
          </span>
        )}
      </Td>
      <Td align="end" className="tabular">
        {s.notWorkingMinutes > 0 ? (
          <>
            <span className="whitespace-nowrap text-error-fg dark:text-error-dark">{span(s.notWorkingMinutes)}</span>
            <span className="block text-caption text-faint">{t('attendance.report.stretchCount', { count: stretches })}</span>
          </>
        ) : (
          <span className="text-faint">—</span>
        )}
      </Td>
      <Td align="end" className="whitespace-nowrap tabular font-medium">
        {came ? span(s.workedMinutes) : <span className="font-normal text-faint">—</span>}
        {/* Free Shifts beside the day's hours, never in them (CR-006). */}
        {(row.free?.count ?? 0) > 0 && (
          <span
            className="block text-caption font-normal text-muted"
            title={`${t('attendance.trackedTotal')}: ${span(row.tracked?.totalMinutes ?? s.workedMinutes)}`}
          >
            {t('attendance.freeMinutesShort', { minutes: span(row.free!.workedMinutes) })}
          </span>
        )}
      </Td>
      <Td align="end" className="tabular">
        {target ? (
          <>
            <span className="whitespace-nowrap">{span(target.requiredMinutes)}</span>
            <span
              className={clsx(
                'block whitespace-nowrap text-caption',
                target.state === 'MET' ? 'text-success-fg dark:text-success' : target.state === 'SHORT' ? 'text-error-fg dark:text-error-dark' : 'text-muted',
              )}
            >
              {target.state === 'MET'
                ? target.extraMinutes > 0
                  ? t('attendance.targetMetExtra', { minutes: span(target.extraMinutes) })
                  : t('attendance.targetMet')
                : target.state === 'SHORT'
                  ? t('attendance.targetShort', { minutes: span(target.missingMinutes) })
                  : t('attendance.targetToGo', { minutes: span(target.missingMinutes) })}
            </span>
          </>
        ) : (
          <span className="text-faint">—</span>
        )}
      </Td>
      <Td align="end" className="tabular">
        {penalties.length ? (
          <>
            <span className={clsx('whitespace-nowrap', days ? 'text-error-fg dark:text-error-dark' : 'text-muted')}>
              {days ? formatDays(days, locale) : '—'}
            </span>
            {penalties.some((p) => p.status === 'PENDING') && (
              <span className="block text-caption text-warning-fg dark:text-warning-dark">{enumLabel('PENDING', locale)}</span>
            )}
          </>
        ) : (
          <span className="text-faint">—</span>
        )}
      </Td>
    </Tr>
  );
}

/** Everything about one person's day: the bar, each shift with its breaks and stretches, and the verdict. */
function DayDetail({ row, range, now }: { row: ReportRow; range: { from: number; to: number } | null; now: number }) {
  return (
    <div className="space-y-4">
      {range && <DayBar row={row} range={range} now={now} />}
      {row.sessions.map((shift, index) => (
        <ShiftDetail key={shift.id} shift={shift} n={index + 1} now={now} />
      ))}
      <DayVerdict row={row} />
      {row.free && row.free.count > 0 && <FreeDetail free={row.free} tracked={row.tracked ?? null} />}
    </div>
  );
}

/** The day's Free Shifts (CR-006), after its verdict, which none of them is in. */
function FreeDetail({ free, tracked }: { free: NonNullable<ReportRow['free']>; tracked: ReportRow['tracked'] | null }) {
  const { t, locale } = useI18n();
  const span = (minutes: number) => formatMinutes(minutes, locale);
  return (
    <div className="rounded-md border hairline px-4 py-3">
      <p className="text-body-sm font-medium text-[var(--text-primary)]">{t('attendance.freeShifts')}</p>
      <p className="text-caption text-muted">{t('attendance.freeShiftsAfterHint')}</p>
      <ul className="mt-2 space-y-1 text-body-sm">
        {free.sessions.map((f) => (
          <li key={f.id} className="flex flex-wrap items-baseline gap-x-2 tabular">
            <Link to={`/attendance/sessions/${f.id}`} className="text-primary-600 hover:underline dark:text-primary-400">
              {t('attendance.freeShiftNumber', { n: f.number })}
            </Link>
            <span className="text-muted">
              <TimeRange from={f.startedAt} to={f.endedAt} />
            </span>
            <span>{span(f.summary.workedMinutes)}</span>
            {f.summary.notWorkingMinutes > 0 && (
              <span className="text-caption text-error-fg dark:text-error-dark">
                {t('attendance.notWorkingShort', { minutes: span(f.summary.notWorkingMinutes) })}
              </span>
            )}
            {!f.endedAt && <span className="text-caption text-faint">{t('attendance.freeSoFar')}</span>}
          </li>
        ))}
      </ul>
      {tracked && (
        <p className="mt-2 text-caption text-muted tabular">
          {t('attendance.trackedTotal')} {span(tracked.totalMinutes)} ({t('attendance.trackedTotalHint')})
        </p>
      )}
    </div>
  );
}

/**
 * The day as a bar across the report's hours: the rostered shift outlined, work filled, and on it
 * the breaks, the pauses and the stretches of Not working. It runs with the reading direction —
 * right to left in Arabic.
 */
function DayBar({ row, range, now }: { row: ReportRow; range: { from: number; to: number }; now: number }) {
  const { t, locale, localized } = useI18n();
  const width = range.to - range.from;
  const at = (moment: number) => ((moment - range.from) / width) * 100;
  const place = (from: number, to: number): CSSProperties => ({
    insetInlineStart: `${at(from)}%`,
    width: `${Math.max(0.4, at(to) - at(from))}%`,
  });
  const hours = width / 3_600_000;
  const step = Math.max(1, Math.ceil(hours / 8));
  const ticks: number[] = [];
  for (let moment = range.from; moment <= range.to; moment += step * 3_600_000) ticks.push(moment);
  const label = (from: number, to: number, what: string) =>
    `${what} · ${formatShortTime(new Date(from), locale)}–${formatShortTime(new Date(to), locale)} (${formatMinutes(minutesBetween(from, to), locale)})`;

  return (
    <div>
      <div className="relative h-8 overflow-hidden rounded-sm bg-[var(--surface-card)] ring-1 ring-inset ring-[var(--line)]">
        {row.roster?.startAt && row.roster.endAt && (
          <div
            className="absolute inset-y-0 rounded-sm border-2 border-dashed border-[var(--line-strong)]"
            style={place(ms(row.roster.startAt), ms(row.roster.endAt))}
            title={label(ms(row.roster.startAt), ms(row.roster.endAt), t('attendance.report.legend.roster'))}
          />
        )}
        {row.sessions.map((shift) => {
          const end = shift.endedAt ? ms(shift.endedAt) : now;
          return (
            <Fragment key={shift.id}>
              <div
                className="absolute inset-y-1.5 rounded-xs bg-success"
                style={place(ms(shift.startedAt), end)}
                title={label(ms(shift.startedAt), end, t('attendance.working'))}
              />
              {shift.breaks.map((b) => {
                const { from, to } = spanOf(b, shift, now);
                const kind = isPause(b) ? 'paused' : b.breakType.countsAsWork ? 'workBreak' : 'break';
                return (
                  <div
                    key={b.id}
                    className={clsx(
                      'absolute inset-y-1.5',
                      kind === 'paused' ? 'bg-neutral-400 dark:bg-neutral-500' : kind === 'workBreak' ? 'bg-info' : 'bg-warning',
                    )}
                    style={place(from, to)}
                    title={label(from, to, isPause(b) ? t('attendance.paused') : localized(b.breakType))}
                  />
                );
              })}
              {(shift.idleSessions ?? []).map((i) => {
                const { from, to } = spanOf(i, shift, now);
                return (
                  <div
                    key={i.id}
                    className="absolute inset-y-1.5 bg-error"
                    style={place(from, to)}
                    title={label(from, to, `${t('attendance.notWorking')} — ${t(`presence.reason.${i.reason}` as TranslationKey)}`)}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </div>
      <div className="relative mt-1 h-4 text-caption text-faint tabular" aria-hidden>
        {ticks.map((moment, index) => (
          <span
            key={moment}
            className={clsx(
              'absolute top-0 whitespace-nowrap',
              index === 0 ? '' : index === ticks.length - 1 && moment === range.to ? '-translate-x-full rtl:translate-x-full' : '-translate-x-1/2 rtl:translate-x-1/2',
            )}
            style={{ insetInlineStart: `${at(moment)}%` }}
          >
            {formatShortTime(new Date(moment), locale)}
          </span>
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption text-muted">
        {(
          [
            ['bg-success', 'attendance.working'],
            ['bg-warning', 'attendance.report.break'],
            ['bg-info', 'attendance.report.legend.workBreak'],
            ['bg-neutral-400 dark:bg-neutral-500', 'attendance.paused'],
            ['bg-error', 'attendance.notWorking'],
          ] as Array<[string, TranslationKey]>
        ).map(([swatch, key]) => (
          <li key={key} className="inline-flex items-center gap-1.5">
            <span className={clsx('h-2.5 w-2.5 rounded-xs', swatch)} aria-hidden />
            {t(key)}
          </li>
        ))}
        {row.roster?.startAt && (
          <li className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-3.5 rounded-xs border-2 border-dashed border-[var(--line-strong)]" aria-hidden />
            {t('attendance.report.legend.roster')}
          </li>
        )}
      </ul>
    </div>
  );
}

/** A penalty: the days, where it stands, and a way to its page. */
function PenaltyTag({ penalty }: { penalty: PenaltyRef }) {
  const { locale } = useI18n();
  return (
    <Link to={`/violations/penalties/${penalty.id}`} className="inline-flex items-center gap-1.5 hover:underline">
      <span className={clsx('whitespace-nowrap tabular', penalty.status === 'CANCELLED' ? 'text-muted line-through' : 'text-error-fg dark:text-error-dark')}>
        {formatDays(penalty.days, locale)}
      </span>
      <Badge tone={statusTone(penalty.status)}>{enumLabel(penalty.status, locale)}</Badge>
    </Link>
  );
}

/** One shift: when, on which computer, how it ended, its breaks and its stretches of Not working. */
function ShiftDetail({ shift, n, now }: { shift: ReportShift; n: number; now: number }) {
  const { t, locale, localized } = useI18n();
  const span = (minutes: number) => formatMinutes(minutes, locale);
  const stretches = shift.idleSessions ?? [];
  const range = (from: number, to: number, running: boolean) => <TimeRange from={from} to={running ? null : to} />;

  return (
    <section className="surface rounded-md">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b hairline px-4 py-2.5">
        <h4 className="text-body-sm font-semibold text-[var(--text-primary)]">{t('attendance.report.shiftN', { n })}</h4>
        <span className="tabular text-body-sm text-[var(--text-primary)]">
          {range(ms(shift.startedAt), shift.endedAt ? ms(shift.endedAt) : now, !shift.endedAt)}
        </span>
        {!shift.endedAt && (
          <Badge tone="success" dot>
            {t('attendance.stillOpen')}
          </Badge>
        )}
        <span className="text-caption text-muted">
          {shift.agentDevice ? t('attendance.trackedBy', { name: shift.agentDevice.name }) : t('attendance.notTracked')}
        </span>
        {shift.endUnconfirmed ? (
          <span className="text-caption text-warning-fg dark:text-warning-dark">{t('attendance.endUnconfirmed')}</span>
        ) : shift.autoClosed ? (
          <span className="text-caption text-warning-fg dark:text-warning-dark">{t('attendance.autoClosed')}</span>
        ) : shift.closedBy ? (
          <span className="text-caption text-warning-fg dark:text-warning-dark">
            {t('attendance.closedBy', { name: localized(shift.closedBy) })}
            {shift.closeNote && (
              <>
                {' — '}
                <span dir="auto">“{shift.closeNote}”</span>
              </>
            )}
          </span>
        ) : null}
        <span className="ms-auto inline-flex gap-4 text-body-sm">
          {shift.endUnconfirmed && (
            <Link to={`/attendance/sessions/${shift.id}/end`} className="text-primary-600 hover:underline dark:text-primary-400">
              {t('attendance.setEnd')}
            </Link>
          )}
          <Link to={`/attendance/sessions/${shift.id}`} className="text-primary-600 hover:underline dark:text-primary-400">
            {t('attendance.details')}
          </Link>
        </span>
      </header>

      <div className="grid gap-x-6 gap-y-4 p-4 2xl:grid-cols-2">
        <div className="min-w-0">
          <h5 className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('attendance.breaks')}</h5>
          {shift.breaks.length === 0 ? (
            <p className="text-body-sm text-muted">{t('attendance.report.noBreaks')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('attendance.report.break')}</Th>
                  <Th>{t('attendance.report.time')}</Th>
                  <Th align="end">{t('attendance.report.length')}</Th>
                  <Th align="end">{t('attendance.report.allowance')}</Th>
                  <Th align="end">{t('attendance.report.over')}</Th>
                  <Th align="end">{t('attendance.penaltyCol')}</Th>
                </tr>
              </thead>
              <tbody>
                {shift.breaks.map((b) => {
                  const { from, to, minutes } = spanOf(b, shift, now);
                  const pause = isPause(b);
                  const over = pause ? 0 : overBy(minutes, b.breakType.maxMinutes);
                  return (
                    <Tr key={b.id}>
                      <Td>
                        {pause ? t('attendance.paused') : localized(b.breakType)}
                        {!pause && b.breakType.countsAsWork && (
                          <span className="block text-caption text-muted">{t('attendance.countsAsWork')}</span>
                        )}
                      </Td>
                      <Td className="tabular text-muted">{range(from, to, !b.endedAt && !shift.endedAt)}</Td>
                      <Td align="end" className="whitespace-nowrap tabular">
                        {span(minutes)}
                      </Td>
                      <Td align="end" className="whitespace-nowrap tabular text-muted">
                        {pause ? '—' : b.breakType.maxMinutes != null ? span(b.breakType.maxMinutes) : t('attendance.report.noLimit')}
                      </Td>
                      <Td align="end" className={clsx('whitespace-nowrap tabular', over ? 'text-error-fg dark:text-error-dark' : 'text-faint')}>
                        {over ? `+${span(over)}` : '—'}
                      </Td>
                      <Td align="end">{b.penalty ? <PenaltyTag penalty={b.penalty} /> : <span className="text-faint">—</span>}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        <div className="min-w-0">
          <h5 className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('attendance.notWorkingStretches')}</h5>
          {stretches.length === 0 ? (
            <p className="text-body-sm text-muted">{shift.agentDevice ? t('attendance.noStretchesHint') : t('attendance.notTracked')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('attendance.stretch')}</Th>
                  <Th align="end">{t('attendance.report.length')}</Th>
                  <Th>{t('attendance.reason')}</Th>
                  <Th align="end">{t('attendance.penaltyCol')}</Th>
                </tr>
              </thead>
              <tbody>
                {stretches.map((i) => {
                  const { from, to, minutes } = spanOf(i, shift, now);
                  return (
                    <Tr key={i.id}>
                      <Td className="tabular text-muted">{range(from, to, !i.endedAt && !shift.endedAt)}</Td>
                      <Td align="end" className="whitespace-nowrap tabular text-error-fg dark:text-error-dark">
                        {span(minutes)}
                      </Td>
                      <Td>{t(`presence.reason.${i.reason}` as TranslationKey)}</Td>
                      <Td align="end">
                        {i.penalty ? (
                          <PenaltyTag penalty={i.penalty} />
                        ) : i.penaltyOutcome ? (
                          <span className="text-caption text-muted">{t(`attendance.outcome.${i.penaltyOutcome}` as TranslationKey)}</span>
                        ) : i.penaltyDueAt ? (
                          <span className="text-caption text-muted">
                            {t('attendance.penaltyDueAt', { time: formatShortTime(i.penaltyDueAt, locale) })}
                          </span>
                        ) : (
                          <span className="text-faint">—</span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </section>
  );
}

/** What the day came to against the Daily hours, and every penalty it raised. */
function DayVerdict({ row }: { row: ReportRow }) {
  const { t, locale } = useI18n();
  const span = (minutes: number) => formatMinutes(minutes, locale);
  const target = row.target;
  const penalties = dayPenalties(row);
  const worked = span(row.summary.workedMinutes);

  return (
    <section className="surface rounded-md px-4 py-3">
      <h4 className="text-overline uppercase text-[var(--text-tertiary)]">{t('attendance.report.dayVerdict')}</h4>
      <p
        className={clsx(
          'mt-1 text-body-sm font-medium tabular',
          target?.state === 'MET' ? 'text-success-fg dark:text-success' : target?.state === 'SHORT' ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]',
        )}
      >
        {!target
          ? t('attendance.report.verdictNotOwed')
          : target.state === 'MET'
            ? t('attendance.report.verdictMet', { worked, owed: span(target.requiredMinutes) })
            : target.state === 'SHORT'
              ? t('attendance.report.verdictShort', { worked, owed: span(target.requiredMinutes), missing: span(target.missingMinutes) })
              : t('attendance.report.verdictRunning', { worked, owed: span(target.requiredMinutes), missing: span(target.missingMinutes) })}
      </p>
      {/* A forgotten shift no agent tracked: its day waits for a manager to set the real end. */}
      {row.sessions.some((s) => s.endUnconfirmed) ? (
        <p className="mt-0.5 text-caption text-warning-fg dark:text-warning-dark">{t('attendance.setEndNote')}</p>
      ) : (
        target &&
        !target.decided &&
        target.state !== 'IN_PROGRESS' && <p className="mt-0.5 text-caption text-muted">{t('attendance.report.notJudgedYet')}</p>
      )}
      {(row.review?.waivedCount ?? 0) > 0 && (
        <p className="mt-0.5 text-caption text-muted">{t('attendance.report.waived', { count: row.review?.waivedCount ?? 0 })}</p>
      )}
      {penalties.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-body-sm">
          {penalties.map((p) => (
            <li key={p.id} className="inline-flex items-center gap-2">
              <span className="text-muted">{t(`attendance.report.kind.${p.kind}` as TranslationKey)}</span>
              <PenaltyTag penalty={p} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
